import { useState } from "react";
import { Link } from "react-router-dom";
import { Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { RewardsSection } from "./RewardsSection";
import { toast } from "sonner";

const CHANNELS = [["balcao", "Balcão / PDV", "Inclui Venda ticket, encomendas e estacionamento."], ["mesas", "Mesas e comandas", "No fechamento da conta com cliente identificado."],
  ["cardapio", "Cardápio digital", "Pedidos de delivery e retirada feitos pelo cliente."], ["interno", "Pedidos internos", "Lançados pela loja (telefone ou WhatsApp)."]];
const NUMS = ["points_per_real", "cashback_pct", "expiry_days", "welcome_points", "free_delivery_points", "expiry_warn_days"];

function Card({ title, text, action, children, testId }) {
  return (
    <section className="card-surface space-y-4 p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 border-b pb-4">
        <div><h3 className="font-display text-lg font-semibold">{title}</h3><p className="text-sm text-muted-foreground">{text}</p></div>{action}
      </div>
      {children}
    </section>
  );
}

function Check({ checked, onChange, title, text, testId, children }) {
  return (
    <div className="flex gap-3">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" data-testid={testId} />
      <div className="space-y-1.5 text-sm"><p className="font-medium leading-none">{title}</p>{text && <p className="text-muted-foreground">{text}</p>}{children}</div>
    </div>
  );
}

const PtsInput = ({ value, onChange, disabled, testId, suffix = "pontos" }) => (
  <div className="flex items-center gap-2"><Input type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="h-9 w-28" data-testid={testId} /><span>{suffix}</span></div>
);

function ProgramCard({ s, set }) {
  const pts = s.mode === "points";
  return (
    <Card title="Programa de fidelidade" text={pts ? `Acumula ${s.points_per_real || 0} ponto(s) por real gasto.` : `Devolve ${s.cashback_pct || 0}% de cada compra como saldo.`} testId="loyalty-program-card"
      action={<label className="flex shrink-0 items-center gap-2 text-sm font-medium"><span className={s.active ? "text-emerald-500" : "text-muted-foreground"}>{s.active ? "Ativado" : "Desativado"}</span><Switch checked={s.active} onCheckedChange={(v) => set("active", v)} data-testid="loyalty-active-switch" /></label>}>
      <div className="space-y-1.5"><p className="text-sm font-medium">Tipo de programa</p>
        <div className="grid grid-cols-2 gap-2">{[["points", "Pontos"], ["cashback", "Cashback"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => set("mode", k)} data-testid={`loyalty-mode-${k}`}
            className={cn("rounded-lg border px-3 py-2 text-sm font-medium transition-colors", s.mode === k ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted")}>{l}</button>
        ))}</div>
      </div>
      {pts ? <Field label="Pontos por R$ 1,00 gasto"><Input type="number" min="0" step="0.1" value={s.points_per_real} onChange={(e) => set("points_per_real", e.target.value)} data-testid="loyalty-points-per-real" /></Field>
        : <Field label="Cashback (% da compra)"><Input type="number" min="0" max="100" step="0.5" value={s.cashback_pct} onChange={(e) => set("cashback_pct", e.target.value)} data-testid="loyalty-cashback-pct" /></Field>}
      <Field label={pts ? "Quanto tempo os pontos ficam disponíveis? (dias)" : "Quanto tempo o cashback fica disponível? (dias)"} hint="Use 0 para não vencer. A contagem começa ao realizar uma compra.">
        <Input type="number" min="0" value={s.expiry_days} onChange={(e) => set("expiry_days", e.target.value)} data-testid="loyalty-expiry-days" />
      </Field>
    </Card>
  );
}

function BenefitsCard({ s, set }) {
  return (
    <Card title="Benefícios" text="Vantagens extras dentro do programa." testId="loyalty-benefits-card">
      {s.mode !== "points" ? <p className="text-sm text-muted-foreground">Disponível no programa de pontos.</p> : (<>
        <Check checked={s.welcome_enabled} onChange={(v) => set("welcome_enabled", v)} title="Bônus de boas-vindas" text="Ao se cadastrar o cliente ganha:" testId="loyalty-welcome-check">
          <PtsInput value={s.welcome_points} onChange={(v) => set("welcome_points", v)} disabled={!s.welcome_enabled} testId="loyalty-welcome-points" />
        </Check>
        <Check checked={s.free_delivery_enabled} onChange={(v) => set("free_delivery_enabled", v)} title="Entrega grátis com pontos" text="O cliente troca pontos pela taxa de entrega." testId="loyalty-free-delivery-check">
          <PtsInput value={s.free_delivery_points} onChange={(v) => set("free_delivery_points", v)} disabled={!s.free_delivery_enabled} testId="loyalty-free-delivery-points" />
        </Check>
      </>)}
    </Card>
  );
}

function ChannelsCard({ s, set }) {
  return (
    <Card title="Pedidos que pontuam" text="Selecione os canais que acumulam pontos." testId="loyalty-channels-card">
      {CHANNELS.map(([k, l, t]) => <Check key={k} checked={s.channels[k] !== false} onChange={(v) => set("channels", { ...s.channels, [k]: v })} title={l} text={t} testId={`loyalty-channel-${k}`} />)}
    </Card>
  );
}

function NotifyCard({ s, set }) {
  const valid = Number(s.expiry_days) > 0;
  return (
    <Card title="Avisos no WhatsApp" text="Mensagens automáticas pelo WhatsApp da loja." testId="loyalty-notify-card">
      {!s.whatsapp_connected && <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400" data-testid="loyalty-wa-warning">O WhatsApp da loja não está conectado. <Link to="/app/configuracoes?aba=whatsapp" className="font-semibold underline">Conectar agora</Link></p>}
      <Check checked={s.notify_earn} onChange={(v) => set("notify_earn", v)} title="Avisar quando ganhar pontos" text="Envia o quanto ganhou e o saldo atualizado." testId="loyalty-notify-earn" />
      <Check checked={s.notify_expiry} onChange={(v) => set("notify_expiry", v)} title="Avisar antes de vencer" text={valid ? "Quantos dias antes:" : "Defina a validade para usar este aviso."} testId="loyalty-notify-expiry">
        {valid && <PtsInput value={s.expiry_warn_days} onChange={(v) => set("expiry_warn_days", v)} disabled={!s.notify_expiry} testId="loyalty-warn-days" suffix="dias antes" />}
      </Check>
    </Card>
  );
}

export function LoyaltySettings({ cfg, setCfg }) {
  const [s, setS] = useState(cfg);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...s, ...Object.fromEntries(NUMS.map((k) => [k, Number(s[k]) || 0])) };
      body.expiry_warn_days = Math.max(1, body.expiry_warn_days);
      const { data } = await api.put("/loyalty/settings", body);
      setCfg(data);
      setS(data);
      toast.success("Configurações da fidelidade salvas");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="loyalty-settings">
      <div className="grid gap-4 md:grid-cols-2">
        <ProgramCard s={s} set={set} /><BenefitsCard s={s} set={set} /><ChannelsCard s={s} set={set} /><NotifyCard s={s} set={set} />
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="h-11 px-6" data-testid="loyalty-save-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Salvar configurações</Button>
      </div>
      {cfg.mode === "points" ? <RewardsSection />
        : <p className="card-surface p-5 text-sm text-muted-foreground" data-testid="loyalty-cashback-note">No cashback o cliente usa o saldo em R$ direto no pagamento. As recompensas (descontos e produtos) ficam disponíveis no programa de pontos.</p>}
    </div>
  );
}
